import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, tap, switchMap } from 'rxjs/operators';
import urlJoin from "url-join";
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { ArbitrosService } from 'src/app/arbitros/arbitros.service';

// utility
import { loggedInUser, encodeToken, decodeToken } from '../helpers/utils';


// types
import { User } from '../models/auth.models';
import { environment } from 'src/environments/environment';
import { NavigationExtras } from '@angular/router';


@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  user: User | null = null;
  private loginURL: string = urlJoin(environment.apiURL, 'usuarios/login')
  private signupURL: string = urlJoin(environment.apiURL, 'usuarios/registro/arbitro')
  private resetPassURL: string = urlJoin(environment.apiURL, 'usuarios/reset-pass-activate')
  private requestResetPassURL: string = urlJoin(environment.apiURL, 'usuarios/request-pass-reset')
  private checkTokenURL: string = urlJoin(environment.apiURL, 'usuarios/validarTokenEmail')
  private confirmEmailChangeURL: string = urlJoin(environment.apiURL, 'usuarios/cambiar-email')


  constructor(private http: HttpClient,
    private router: Router,
    private arbitrosService: ArbitrosService) {
  }

  /**
   * Returns the current user
   */
  public currentUser(): Observable<User | null> {
    if (this.user) {
      // Si ya tenemos al usuario, simplemente devuélvelo envuelto en un Observable
      return of(this.user);
    } else {
      // Suponiendo que loggedInUser() devuelve un objeto User o null de manera sincrónica
      const user = loggedInUser();
      if (user && user.id) {
        // Asigna el usuario antes de las operaciones asíncronas para evitar repetir este paso
        this.user = user;
        // Devuelve un Observable que se resuelve con el usuario actualizado después de obtener la información adicional
        return this.arbitrosService.getArbitro(user.id).pipe(
          switchMap((res) => {
            if (this.user) { // Asegura que this.user esté definido
              this.user.id = res.id;
              this.user.firstName = res.nombre;
              this.user.lastName = res.apellidos;
              this.user.nivel_acceso = res.nivel_acceso;
              this.user.es_arbitro = res.es_arbitro;
              // Aquí puedes agregar más lógica si es necesario
            }
            return of(this.user);
          }),
          catchError((error) => {
            console.error('Error al obtener información del árbitro:', error);
            // Maneja el error como consideres adecuado
            return of(null);
          })
        );
      } else {
        // Devuelve null si no hay un usuario o falta el ID
        return of(null);
      }
    }
  }

  /**
   * Performs the login auth
   * @param email email of user
   * @param password password of user
   */
  login(email: string, pass: string): Observable<User> {
    return this.http.post(this.loginURL, { email, pass })
      .pipe(
        tap((response: any) => {
          if (response.token) {
            const encodedToken = encodeToken(response.token)
            sessionStorage.setItem('currentUser', encodedToken);

            // jwtDecode Token
            //let jwttoken = jwtDecode(response.token);
            //console.log("AuthService decoded token->", jwttoken)
            //console.log("AuthServiceresponse.token->", response.token)

          }
        }),
        catchError(error => {
          //this.toastr.error('Error en acceso: ' + error.error.message);    
          return throwError(() => error);
        })
      )
  }

  /**
   * Performs the signup auth
   * @param nombre
   * @param apellidos 
   * @param email 
   */
  signup(nombre: string, apellidos: string, email: string, genero: string): Observable<User> {
    return this.http.post(this.signupURL, { nombre, apellidos, email, genero, es_arbitro: true }) // Fuerzo a que sea árbitro
      .pipe(
        tap((response: any) => {

        }),
        catchError(error => {
          //this.toastr.error('Error en acceso: ' + error.error.message);    
          return throwError(() => error);
        })
      )
  }



  /**
   * Logout the user
   */
  logout(): void {
    // remove user from session storage to log user out
    sessionStorage.removeItem('currentUser');
    this.user = null;
  }

  resetPassword(token: string, newPassword: string, confirmPassword: string): Observable<any> {
    // Crear un encabezado con el token
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + token
    });

    return this.http.put(this.resetPassURL, { newPassword, confirmPassword }, { headers })
      .pipe(
        tap((response: any) => {
          //this.toastr.success('La cuenta ha sido validada correctamente');
        }),
        catchError(error => {
          // Si la respuesta falla, redirijo a /login
          //this.router.navigate(['/auth/login']);      
          return throwError(() => error)
        })
      )
  }


  requestResetPassword(email: string): Observable<any> {

    return this.http.post(this.requestResetPassURL, { email })
      .pipe(
        tap((response: any) => {
          //this.toastr.success('La cuenta ha sido validada correctamente');
        }),
        catchError(error => {
          // Si la respuesta falla, redirijo a /login
          //this.router.navigate(['/auth/login']);      
          return throwError(() => error)
        })
      )
  }

  checkToken(token: string): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + token
    });    

    return this.http.get(this.checkTokenURL, { headers })
      .pipe(
        tap((response: any) => {
          // Puedes agregar lógica si necesitas realizar alguna acción en caso de éxito
        }),
        catchError(error => {
          // Si hay un error, se lanza para ser manejado por el componente que llama a esta función
          return throwError(() => error);
        })
      );
  }

  confirmEmailChange(token: string): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': 'Bearer ' + token
    });

    return this.http.put(this.confirmEmailChangeURL, {}, { headers })
      .pipe(
        tap((response: any) => {
          // Puedes agregar lógica si necesitas realizar alguna acción en caso de éxito
        }),
        catchError(error => {
          // Si hay un error, se lanza para ser manejado por el componente que llama a esta función
          return throwError(() => error);
        })
      );
  }

}
